import { PackItemList, Title } from '@bpm-web-app/components';
import { useInfiniteSearchPack } from '@bpm-web-app/swr-hooks';
import { convertSoundPackageToCarouselProps, convertSoundPackageToSeeMoreLayoutProps, createAppRoutes, soundPackageToCreatePlayable, useCreateFilterParams, useHubSwitch } from '@bpm-web-app/utils';
import { useMemo, useCallback, useEffect } from 'react';
import { ClickableGenres } from '../clickable-genres/clickable-genres';
import { CreateCardCarousel } from '../create-card-carousel/create-card-carousel';
import { useCreatePlayer } from '../create-player/create-player';
import SeeMoreLayout from '../see-more-layout/see-more-layout';
import { LoadMore } from '../shared';
import { MusicViewProps } from './music-view-props';

export function PacksMusicView(packsMusicProps: MusicViewProps) {
    const { createQuery, title, seeMorePath, variant, onTotalCount, isLoading, setIsLoading } = packsMusicProps;
    const { hub } = useHubSwitch();
    const { playDemo } = useCreatePlayer();
    const pageQuery = useCreateFilterParams(createQuery);

    const { data: packsData = [], isLoadingInitialData: isLoadingPacks, isLoadingMore, isLastPage, setSize, } = useInfiniteSearchPack(pageQuery);

    const flatPackData = useMemo(() => packsData?.flatMap((paginated) => paginated?.data) || [], [packsData]);

    const carouselData = useMemo(() => flatPackData?.map((pack) => convertSoundPackageToCarouselProps(pack)), [flatPackData]);

    const handlePlay = useCallback(
        (id: string) => {
            const soundPack = flatPackData.find((pack) => pack.id === id);
            if (soundPack) playDemo(soundPackageToCreatePlayable(soundPack));
        },
        [flatPackData, playDemo]
    );
    const handleLoadMore = useCallback(() => {
        if (!isLoadingMore || !isLastPage || !isLoadingPacks) {
            setSize((prevSize) => prevSize + 1);
        }
    }, [isLastPage, isLoadingPacks, isLoadingMore, setSize]);

    useEffect(() => {
        if (packsData[0]?.pagination?.total !== undefined && onTotalCount) {
            onTotalCount(packsData[0]?.pagination?.total || 0);
        }
    }, [onTotalCount, packsData]);

    useEffect(() => {
        if (setIsLoading) {
            setIsLoading(isLoadingPacks);
        }
    }, [isLoadingPacks, setIsLoading]);

    return variant === 'component' ?
        (
            <CreateCardCarousel
                contentType="pack"
                items={carouselData}
                cardSize="small"
                carouselTitle={title ?? 'Packs'}
                carouselMorePath={seeMorePath}
                itemBasePath={createAppRoutes.packs}
                onCardPlay={handlePlay}
                noResultsText="No Packs Available"
                isLoading={isLoading || isLoadingPacks}
            />
        ) :
        variant === 'fullscreen' ? (
            <>
                <Title platform={hub} title={flatPackData[0]?.Genre?.name} />
                <SeeMoreLayout
                    contentType="pack"
                    pageTitle={title}
                    count={packsData?.[0]?.pagination?.total ?? 0}
                    itemBasePath={createAppRoutes.packs}
                    data={flatPackData?.map((item) => convertSoundPackageToSeeMoreLayoutProps(item, <ClickableGenres genres={item.Genre} />))}
                />
                <LoadMore onLoadMore={handleLoadMore} />
            </>
        ) : (
            <PackItemList data={flatPackData} onLoadMore={handleLoadMore} />
        );
}
